// The sections of one brief: executive page, advice (super admin — the server
// never sends it to anyone else), per module, per branch, data quality.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Info, ShieldAlert, ExternalLink, Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AI } from "@/constants/testIds/ai";
import { RichText, TONE_DOT } from "@/components/ai-analysis/aiText";

function branchName(brief, code, lang, t) {
    if (!code || code === "network") return t("ai_network");
    const b = (brief.branches || {})[code];
    if (!b) return code;
    return lang === "ar" ? b.ar || b.en : b.en || b.ar;
}

function moduleName(brief, key, lang) {
    const m = (brief.modules || []).find((x) => x.key === key);
    return (m && (lang === "ar" ? m.name_ar : m.name_en)) || key;
}

const errText = (e, lang) => (lang === "ar" ? e.message_ar || e.message_en : e.message_en || e.message_ar) || e.code;
const partErrors = (brief, part) => (brief.errors || []).filter((e) => e.part === part);

/** Which part failed and why (the server's own message), with the modules / branches a
 *  detail chunk covered — instead of a bare "partly done". */
export function PartialBanner({ brief, lang, t }) {
    const errors = brief.errors || [];
    if (!errors.length) return null;
    const scopeNames = (e) => (e.scope || []).map((k) => ((brief.branches || {})[k] ? branchName(brief, k, lang, t) : moduleName(brief, k, lang)));
    return (
        <div data-testid={AI.failed} className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 space-y-1">
                <p className="font-semibold">{t("ai_partial")}</p>
                <ul className="list-inside list-disc space-y-0.5">
                    {errors.map((e, i) => {
                        const names = scopeNames(e);
                        return (
                            <li key={i} data-testid={AI.failedItem} data-part={e.part} data-code={e.code}>
                                <span className="font-semibold">{t(`ai_part_${e.part}`)}</span>: {errText(e, lang)}
                                {names.length ? <span className="text-xs"> ({names.join(lang === "ar" ? "، " : ", ")})</span> : null}
                            </li>
                        );
                    })}
                </ul>
                <p className="text-xs">{t("ai_retry_hint")}</p>
            </div>
        </div>
    );
}

/** A part of the brief with no text: say WHY (the part failed, or the model wrote nothing),
 *  never "no data" — the modules' data itself is listed under «جودة البيانات». */
function PartMissing({ brief, part, lang, t }) {
    const errs = partErrors(brief, part);
    return (
        <div data-testid={AI.partMissing} data-part={part} className="rounded-lg surface-2 p-4 text-sm text-[color:var(--ink-muted)]">
            <p className="font-semibold text-[color:var(--ink)]">{t("ai_part_missing")}</p>
            {errs.length ? <p className="mt-1">{errText(errs[0], lang)}</p> : null}
            {part === "detail" ? <p className="mt-1 text-xs">{t("ai_detail_missing_hint")}</p> : null}
        </div>
    );
}

function Point({ p, brief, lang, t, showBranch = true }) {
    return (
        <li data-testid={AI.point} data-tone={p.tone} className="flex items-start gap-2.5 py-1.5 leading-relaxed">
            <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${TONE_DOT[p.tone] || TONE_DOT.neutral}`} />
            <span className="min-w-0 text-sm text-[color:var(--ink)]">
                {showBranch && p.branch && p.branch !== "network" ? (
                    <span className="me-1.5 font-semibold text-[color:var(--brand-brown)]">{branchName(brief, p.branch, lang, t)}:</span>
                ) : null}
                <RichText text={p.text} flags={p.flags} figures={brief.figures} lang={lang} t={t} />
            </span>
        </li>
    );
}

function Points({ points, brief, lang, t, showBranch }) {
    if (!points?.length) return <p className="text-sm text-[color:var(--ink-faint)]">{t("ai_points_none")}</p>;
    return (
        <ul className="divide-y divide-[color:var(--border-soft)]">
            {points.map((p, i) => <Point key={i} p={p} brief={brief} lang={lang} t={t} showBranch={showBranch} />)}
        </ul>
    );
}

export function ExecutiveSection({ brief, lang, t }) {
    const ex = brief.content?.executive;
    if (!ex) return <PartMissing brief={brief} part="executive" lang={lang} t={t} />;
    return (
        <Card data-testid={AI.executive} className="surface">
            <CardContent className="space-y-4 p-4 sm:p-6">
                {ex.headline ? (
                    <h2 data-testid={AI.headline} className="text-lg font-bold leading-snug text-[color:var(--ink)] sm:text-xl">
                        <RichText text={ex.headline.text} flags={ex.headline.flags} figures={brief.figures} lang={lang} t={t} />
                    </h2>
                ) : null}
                {ex.summary ? (
                    <p className="text-sm leading-relaxed text-[color:var(--ink-muted)]">
                        <RichText text={ex.summary.text} flags={ex.summary.flags} figures={brief.figures} lang={lang} t={t} />
                    </p>
                ) : null}
                <Points points={ex.points} brief={brief} lang={lang} t={t} />
            </CardContent>
        </Card>
    );
}

const IMPACT_CLS = {
    high: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
    medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
};

export function AdviceSection({ brief, lang, t }) {
    if (!brief.can_see_advice) {
        return (
            <div data-testid={AI.adviceSuperOnly} className="flex items-center gap-2 rounded-lg surface-2 p-4 text-sm text-[color:var(--ink-muted)]">
                <Lock className="h-4 w-4" /> {t("ai_advice_super_only")}
            </div>
        );
    }
    const items = brief.content?.advice || [];
    if (!items.length) return <p className="text-sm text-[color:var(--ink-faint)]">{t("ai_no_advice")}</p>;
    return (
        <div data-testid={AI.advice} className="space-y-3">
            {items.map((a, i) => (
                <Card key={i} data-testid={AI.adviceCard(a.priority)} className="surface">
                    <CardContent className="space-y-2.5 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--brand-orange)] text-sm font-bold text-white">
                                {a.priority}
                            </span>
                            <Badge variant="secondary">{branchName(brief, a.branch, lang, t)}</Badge>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${IMPACT_CLS[a.impact] || IMPACT_CLS.medium}`}>
                                {t(`ai_impact_${a.impact}`)}
                            </span>
                            <span className="text-[11px] text-[color:var(--ink-faint)]">{t(`ai_horizon_${a.horizon}`)}</span>
                        </div>
                        <h3 className="text-base font-bold leading-snug text-[color:var(--ink)]">
                            <RichText text={a.title} flags={a.flags} figures={brief.figures} lang={lang} t={t} />
                        </h3>
                        <p className="text-sm leading-relaxed">
                            <span className="font-semibold text-[color:var(--brand-brown)]">{t("ai_action")}: </span>
                            <RichText text={a.action} flags={a.flags} figures={brief.figures} lang={lang} t={t} />
                        </p>
                        <p className="text-sm leading-relaxed text-[color:var(--ink-muted)]">
                            <span className="font-semibold">{t("ai_why")}: </span>
                            <RichText text={a.rationale} flags={a.flags} figures={brief.figures} lang={lang} t={t} />
                        </p>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

export function ModulesSection({ brief, lang, t }) {
    const meta = useMemo(() => Object.fromEntries((brief.modules || []).map((m) => [m.key, m])), [brief.modules]);
    const entries = brief.content?.modules || [];
    if (!entries.length) return <PartMissing brief={brief} part="detail" lang={lang} t={t} />;
    // modules whose data was collected but whose text is missing (a failed chunk, or skipped)
    const written = new Set(entries.map((e) => e.module));
    const unwritten = (brief.modules || []).filter((m) => m.status === "ok" && !written.has(m.key));
    return (
        <div className="space-y-2">
            {unwritten.length ? (
                <p data-testid={AI.notWritten} className="rounded-lg surface-2 px-3 py-2 text-xs text-[color:var(--ink-muted)]">
                    {t("ai_not_written")}: {unwritten.map((m) => (lang === "ar" ? m.name_ar : m.name_en)).join(lang === "ar" ? "، " : ", ")}
                </p>
            ) : null}
            <Accordion type="multiple" defaultValue={entries.slice(0, 2).map((e) => e.module)} data-testid={AI.modules}
                       className="space-y-2">
                {entries.map((e) => {
                    const m = meta[e.module] || {};
                    return (
                        <AccordionItem key={e.module} value={e.module} data-testid={AI.module(e.module)}
                                       className="rounded-xl border border-[color:var(--border-soft)] surface px-4">
                            <AccordionTrigger className="text-start hover:no-underline">
                                <span className="min-w-0">
                                    <span className="block font-bold text-[color:var(--ink)]">{(lang === "ar" ? m.name_ar : m.name_en) || e.module}</span>
                                    {e.headline ? (
                                        <span className="mt-0.5 block text-xs font-normal leading-relaxed text-[color:var(--ink-muted)]">
                                            <RichText text={e.headline.text} flags={e.headline.flags} figures={brief.figures} lang={lang} t={t} />
                                        </span>
                                    ) : null}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                                <Points points={e.points} brief={brief} lang={lang} t={t} />
                                {m.route ? (
                                    <Link to={m.route} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-orange)] hover:underline">
                                        <ExternalLink className="h-3.5 w-3.5" /> {t("ai_open_screen")}
                                    </Link>
                                ) : null}
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
}

export function BranchesSection({ brief, lang, t, dir }) {
    const entries = brief.content?.branches || [];
    const [code, setCode] = useState(entries[0]?.branch || "");
    const cur = entries.find((e) => e.branch === code) || entries[0];
    if (!entries.length) return <PartMissing brief={brief} part="detail" lang={lang} t={t} />;
    return (
        <div className="space-y-3">
            <Select value={cur?.branch} onValueChange={setCode} dir={dir}>
                <SelectTrigger data-testid={AI.branchSelect} className="w-full sm:w-72">
                    <SelectValue placeholder={t("ai_choose_branch")} />
                </SelectTrigger>
                <SelectContent>
                    {entries.map((e) => (
                        <SelectItem key={e.branch} value={e.branch}>{branchName(brief, e.branch, lang, t)}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {cur ? (
                <Card data-testid={AI.branch} data-branch={cur.branch} className="surface">
                    <CardContent className="space-y-3 p-4">
                        <h3 className="text-base font-bold text-[color:var(--ink)]">{branchName(brief, cur.branch, lang, t)}</h3>
                        {cur.headline ? (
                            <p className="text-sm leading-relaxed text-[color:var(--ink-muted)]">
                                <RichText text={cur.headline.text} flags={cur.headline.flags} figures={brief.figures} lang={lang} t={t} />
                            </p>
                        ) : null}
                        <Points points={cur.points} brief={brief} lang={lang} t={t} showBranch={false} />
                    </CardContent>
                </Card>
            ) : null}
        </div>
    );
}

const SEV_ICON = { high: ShieldAlert, warn: AlertTriangle, info: Info };
const SEV_CLS = {
    high: "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100",
    warn: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
    info: "border-[color:var(--border-soft)] surface-2 text-[color:var(--ink)]",
};
const SEV_ORDER = { high: 0, warn: 1, info: 2 };

export function QualitySection({ brief, lang, t }) {
    const caveats = [...(brief.caveats || [])].sort((a, b) => (SEV_ORDER[a.severity] ?? 3) - (SEV_ORDER[b.severity] ?? 3));
    const summ = brief.validation?.summary || {};
    const flagged = (summ.detail_flagged || 0) + (summ.executive_flagged || 0) + (summ.advice_flagged || 0);
    const rejected = (summ.detail_rejected || 0) + (summ.executive_rejected || 0) + (summ.advice_rejected || 0);
    const modules = brief.modules || [];
    return (
        <div data-testid={AI.quality} className="space-y-4">
            <section className="space-y-2">
                <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("ai_caveats")}</h3>
                {caveats.length ? caveats.map((c, i) => {
                    const Icon = SEV_ICON[c.severity] || Info;
                    return (
                        <div key={i} data-testid={AI.caveat} data-severity={c.severity}
                             className={`flex items-start gap-2 rounded-lg border p-3 text-sm leading-relaxed ${SEV_CLS[c.severity] || SEV_CLS.info}`}>
                            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{lang === "ar" ? c.ar : c.en}</span>
                        </div>
                    );
                }) : <p className="text-sm text-[color:var(--ink-faint)]">{t("ai_no_caveats")}</p>}
            </section>
            {(flagged || rejected) ? (
                <section className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg surface-2 p-3 text-center">
                        <div className="text-xl font-bold tabular-nums">{flagged}</div>
                        <div className="text-[11px] text-[color:var(--ink-muted)]">{t("ai_flagged_items")}</div>
                    </div>
                    <div className="rounded-lg surface-2 p-3 text-center">
                        <div className="text-xl font-bold tabular-nums">{rejected}</div>
                        <div className="text-[11px] text-[color:var(--ink-muted)]">{t("ai_rejected_items")}</div>
                    </div>
                </section>
            ) : null}
            {modules.length ? (
                <section className="space-y-2">
                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("ai_modules_status")}</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {modules.map((m) => (
                            <span key={m.key} data-status={m.status}
                                  className={`rounded-full border px-2.5 py-1 text-xs ${m.status === "error" ? "border-red-300 text-red-700" : m.status === "empty" ? "border-[color:var(--border-soft)] text-[color:var(--ink-faint)]" : "border-emerald-300 text-emerald-700"}`}>
                                {lang === "ar" ? m.name_ar : m.name_en} · {t(`ai_module_${m.status}`)}
                            </span>
                        ))}
                    </div>
                </section>
            ) : null}
        </div>
    );
}
